// components/TestRunner/rules/Rule08_PBOGroup_Distance_EventsConsistency.js

/**
 * Rule08_PBOGroup_Distance_EventsConsistency.js
 *
 * ✅ Règle 08 – Les fibres d'un même PBO doivent avoir une cohérence dans les événements détectés (positions et longueurs)
 * Type: Error
 *
 * 🧪 Tests effectués :
 * • Regroupement fibres par ropRef.PBO_VALUE
 * • Validation cohérence positions d'événements : POSITIONS_1310/1550 avec tolérance ±2m
 * • Validation cohérence longueurs d'événements : LENGTH_EVENTS_1310/1550 avec tolérance ±2m
 * • Détection incohérences : identification des fibres outliers via algorithme de consensus
 * • Affichage optimisé : format tableau synthétique
 */

export class Rule08_PBOGroup_Distance_EventsConsistency {
  // Constante de tolérance pour les distances
  static DISTANCE_TOLERANCE_METERS = 2;

  /**
   * Appliquer la règle 08 : Cohérence des événements dans les groupes PBO
   * @param {Array} allFibers - Toutes les fibres du projet
   */
  static apply(allFibers) {
    // Prérequis : Rule00 + Rule01 ont validé l'existence des données de base

    // Grouper les fibres par PBO_VALUE
    const pboGroups = this._groupFibersByPBO(allFibers);

    // Analyser chaque groupe pour détecter les incohérences
    Object.entries(pboGroups).forEach(([pboValue, fibers]) => {
      // Skip les groupes avec une seule fibre
      if (fibers.length <= 1) {
        return;
      }

      // Analyser la cohérence des événements du groupe
      const groupAnalysis = this._analyzeGroupEventConsistency(pboValue, fibers);

      // Regrouper les erreurs par type
      const errorsByType = this._groupErrorsByType(groupAnalysis);

      // Générer une erreur pour chaque type de problème détecté
      Object.entries(errorsByType).forEach(([errorType, issues]) => {
        if (issues.length > 0) {
          const errorInfo = this._getErrorInfo(errorType, issues, pboValue);

          // NOUVELLE LOGIQUE: Marquer seulement les fibres outliers
          const outliersToMark = this._identifyOutlierFibers(issues);

          outliersToMark.forEach((fiberId) => {
            const fiber = fibers.find((f) => f.id === fiberId);
            if (fiber) {
              this._addError(fiber, "R08", errorInfo.title, errorInfo.message);
            }
          });
        }
      });
    });
  }

  /**
   * Identifier les fibres outliers à marquer
   * @param {Array} issues - Issues détectées
   * @returns {Array} Liste des IDs de fibres outliers
   */
  static _identifyOutlierFibers(issues) {
    const outlierFibers = new Set();

    issues.forEach((issue) => {
      const clusters = this._findClusters(issue.fiberDetails, this.DISTANCE_TOLERANCE_METERS);
      const consensusCluster = clusters.reduce((prev, current) => (current.count > prev.count ? current : prev));

      // Marquer les fibres des clusters minoritaires
      clusters.forEach((cluster) => {
        if (cluster !== consensusCluster) {
          cluster.values.forEach((fiber) => {
            outlierFibers.add(fiber.fiberId);
          });
        }
      });
    });

    return Array.from(outlierFibers);
  }

  /**
   * Trouver les clusters de valeurs proches
   * @param {Array} fiberDetails - Détails des fibres avec valeurs
   * @param {number} tolerance - Tolérance pour clustering
   * @returns {Array} Clusters identifiés
   */
  static _findClusters(fiberDetails, tolerance) {
    const clusters = [];

    fiberDetails.forEach((item) => {
      let foundCluster = false;

      for (const cluster of clusters) {
        const isInCluster = cluster.values.some((clusterItem) => Math.abs(item.value - clusterItem.value) <= tolerance);

        if (isInCluster) {
          cluster.values.push(item);
          cluster.count++;
          foundCluster = true;
          break;
        }
      }

      if (!foundCluster) {
        clusters.push({ values: [item], count: 1 });
      }
    });

    // Calculer le centre de chaque cluster (médiane)
    clusters.forEach((cluster) => {
      const clusterValues = cluster.values.map((v) => v.value).sort((a, b) => a - b);
      cluster.center = clusterValues[Math.floor(clusterValues.length / 2)];
    });

    return clusters;
  }

  /**
   * Analyser les événements pour génération de tableau
   * @param {Array} issues - Issues détectées
   * @returns {Object} Analyse structurée pour tableau
   */
  static _analyzeEventsForTable(issues) {
    // Regrouper les données par fibre et longueur d'onde
    const fiberData = {};

    issues.forEach((issue) => {
      const wavelength = issue.field.includes("1310") ? "1310nm" : "1550nm";

      issue.fiberDetails.forEach((fiber) => {
        if (!fiberData[fiber.fiberId]) {
          fiberData[fiber.fiberId] = { fiberId: fiber.fiberId, values: [] };
        }
        fiberData[fiber.fiberId].values.push({
          wavelength: wavelength,
          value: fiber.value,
          eventIndex: issue.eventIndex,
        });
      });
    });

    // Analyser consensus pour chaque longueur d'onde
    const values1310 = Object.values(fiberData)
      .map((f) => f.values.find((v) => v.wavelength === "1310nm")?.value)
      .filter((v) => v !== undefined);

    const values1550 = Object.values(fiberData)
      .map((f) => f.values.find((v) => v.wavelength === "1550nm")?.value)
      .filter((v) => v !== undefined);

    // Calculer consensus (médiane) avec vérification de sécurité
    const getMedian = (values) => {
      if (!values || values.length === 0) return 0;
      const sorted = [...values].sort((a, b) => a - b);
      return sorted[Math.floor(sorted.length / 2)];
    };

    const consensus1310 = getMedian(values1310);
    const consensus1550 = getMedian(values1550);

    // Identifier outliers et consensus
    const outliers = [];
    const consensusFibers = [];

    Object.values(fiberData).forEach((fiber) => {
      const val1310 = fiber.values.find((v) => v.wavelength === "1310nm")?.value;
      const val1550 = fiber.values.find((v) => v.wavelength === "1550nm")?.value;

      const dev1310 = val1310 && consensus1310 ? Math.abs(val1310 - consensus1310) : 0;
      const dev1550 = val1550 && consensus1550 ? Math.abs(val1550 - consensus1550) : 0;
      const maxDeviation = Math.max(dev1310, dev1550);

      if (maxDeviation > this.DISTANCE_TOLERANCE_METERS) {
        outliers.push({
          fiberId: fiber.fiberId,
          values: fiber.values,
          maxDeviation: maxDeviation,
        });
      } else {
        consensusFibers.push(fiber);
      }
    });

    return {
      outliers: outliers,
      consensus: {
        count: consensusFibers.length,
        center1310: consensus1310,
        center1550: consensus1550,
        fibers: consensusFibers,
      },
    };
  }

  /**
   * Grouper les fibres par PBO_VALUE
   * @param {Array} allFibers - Toutes les fibres
   * @returns {Object} Groupes de fibres par PBO
   */
  static _groupFibersByPBO(allFibers) {
    const groups = {};

    allFibers.forEach((fiber) => {
      // Vérifier si la fibre a des données DCO (réussite ou échec)
      const hasDcoData = this._hasDcoData(fiber);
      const pboValue = fiber.ropRef?.PBO_VALUE;

      if (hasDcoData && pboValue) {
        if (!groups[pboValue]) {
          groups[pboValue] = [];
        }
        groups[pboValue].push(fiber);
      }
    });

    return groups;
  }

  /**
   * Vérifier si une fibre a des données DCO
   * @param {Object} fiber - Objet fibre
   * @returns {boolean} true si des données DCO existent
   */
  static _hasDcoData(fiber) {
    const hasReussite = fiber.hasDcoPvReussite && fiber.dcoPvReussite;
    const hasEchec = fiber.hasDcoPvEchec && fiber.dcoPvEchec;
    return hasReussite || hasEchec;
  }

  /**
   * Analyser la cohérence des événements d'un groupe de fibres
   * @param {string} pboValue - Valeur PBO du groupe
   * @param {Array} fibers - Fibres du groupe
   * @returns {Object} Résultat de l'analyse
   */
  static _analyzeGroupEventConsistency(pboValue, fibers) {
    const analysis = {
      pboValue: pboValue,
      fiberCount: fibers.length,
      issues: [],
    };

    // Extraire les données de positions de chaque fibre
    const fiberData = fibers.map((fiber) => this._extractEventData(fiber));

    // Analyser la cohérence des positions d'événements
    this._analyzeEventPositions(analysis, fiberData, "positions1310", "POSITIONS_1310");
    this._analyzeEventPositions(analysis, fiberData, "positions1550", "POSITIONS_1550");

    // Analyser la cohérence des longueurs d'événements
    this._analyzeEventPositions(analysis, fiberData, "lengthEvents1310", "LENGTH_EVENTS_1310");
    this._analyzeEventPositions(analysis, fiberData, "lengthEvents1550", "LENGTH_EVENTS_1550");

    return analysis;
  }

  /**
   * Extraire les données d'événements d'une fibre
   * @param {Object} fiber - Objet fibre
   * @returns {Object} Données d'événements extraites
   */
  static _extractEventData(fiber) {
    const dcoData = fiber.hasDcoPvReussite ? fiber.dcoPvReussite : fiber.dcoPvEchec;

    return {
      fiberId: fiber.id,
      type: fiber.hasDcoPvReussite ? "Réussite" : "Échec",
      positions1310: dcoData?.POSITIONS_1310 || [],
      positions1550: dcoData?.POSITIONS_1550 || [],
      lengthEvents1310: dcoData?.LENGTH_EVENTS_1310 || [],
      lengthEvents1550: dcoData?.LENGTH_EVENTS_1550 || [],
    };
  }

  /**
   * Analyser la cohérence des positions/longueurs d'événements index par index
   * @param {Object} analysis - Objet d'analyse à modifier
   * @param {Array} fiberData - Données des fibres
   * @param {string} fieldKey - Clé du champ
   * @param {string} fieldName - Nom du champ pour rapport
   */
  static _analyzeEventPositions(analysis, fiberData, fieldKey, fieldName) {
    // Collecter toutes les valeurs de toutes les fibres (en ignorant le nombre de tentatives)
    const fiberFinalValues = fiberData
      .map((data) => {
        if (Array.isArray(data[fieldKey]) && data[fieldKey].length > 0) {
          // Parcourir toutes les tentatives de mesure
          const lastMeasurement = data[fieldKey][data[fieldKey].length - 1];
          if (Array.isArray(lastMeasurement)) {
            return {
              fiberId: data.fiberId,
              values: lastMeasurement,
            };
          }
        }
        return {
          fiberId: data.fiberId,
          values: [],
        };
      })
      .filter((fiber) => fiber.values.length > 0);

    if (fiberFinalValues.length < 2) {
      return;
    }

    // Trouver le nombre maximum d'événements
    const maxEvents = Math.max(...fiberFinalValues.map((fiber) => fiber.values.length));

    // Comparer chaque index d'événement
    for (let eventIndex = 0; eventIndex < maxEvents; eventIndex++) {
      const valuesAtIndex = [];
      const fiberDetailsAtIndex = [];

      fiberFinalValues.forEach((fiber) => {
        if (fiber.values[eventIndex] !== undefined) {
          const value = fiber.values[eventIndex];
          if (typeof value === "number" && !isNaN(value)) {
            valuesAtIndex.push(value);
            fiberDetailsAtIndex.push({
              fiberId: fiber.fiberId,
              value: value,
            });
          }
        }
      });

      // Analyser la variance pour cet index spécifique
      if (valuesAtIndex.length >= 2) {
        const minValue = Math.min(...valuesAtIndex);
        const maxValue = Math.max(...valuesAtIndex);
        const range = maxValue - minValue;

        if (range > this.DISTANCE_TOLERANCE_METERS) {
          analysis.issues.push({
            type: fieldName.includes("POSITIONS") ? "position_variance" : "length_variance",
            field: fieldName,
            eventIndex: eventIndex,
            range: range,
            tolerance: this.DISTANCE_TOLERANCE_METERS,
            minValue: minValue,
            maxValue: maxValue,
            fiberDetails: fiberDetailsAtIndex,
          });
        }
      }
    }
  }

  /**
   * Regrouper les erreurs par type
   * @param {Object} groupAnalysis - Résultat de l'analyse
   * @returns {Object} Erreurs regroupées par type
   */
  static _groupErrorsByType(groupAnalysis) {
    const errorsByType = {
      position_variance: [],
      length_variance: [],
    };

    groupAnalysis.issues.forEach((issue) => {
      errorsByType[issue.type].push(issue);
    });

    return errorsByType;
  }

  /**
   * Obtenir les informations d'erreur pour un type donné
   * @param {string} errorType - Type d'erreur
   * @param {Array} issues - Problèmes de ce type
   * @param {string} pboValue - Valeur PBO
   * @returns {Object} Informations d'erreur
   */
  static _getErrorInfo(errorType, issues, pboValue) {
    switch (errorType) {
      case "position_variance":
        return {
          title: "Écart de positions d'événements dépassant la tolérance dans le PBO",
          message: this._generatePositionVarianceMessage(issues, pboValue),
        };
      case "length_variance":
        return {
          title: "Écart de longueurs d'événements dépassant la tolérance dans le PBO",
          message: this._generateLengthVarianceMessage(issues, pboValue),
        };
      default:
        return {
          title: "Incohérence d'événements détectée dans le PBO",
          message: "Erreur inconnue détectée",
        };
    }
  }

  /**
   * Générer le message pour erreurs de variance de positions (FORMAT TABLEAU)
   */
  static _generatePositionVarianceMessage(issues, pboValue) {
    let message = `<strong>⚠️ Événements incohérents - ${pboValue}</strong><br><br>`;

    // Utiliser les classes CSS définies dans ReportStyles.js
    message += `<table class="rule08-table">`;
    message += `<thead class="rule08-thead">`;
    message += `<tr class="rule08-header-row">`;
    message += `<th class="rule08-th rule08-th-left">Fibre</th>`;
    message += `<th class="rule08-th rule08-th-center">Événement</th>`;
    message += `<th class="rule08-th rule08-th-center">1310nm</th>`;
    message += `<th class="rule08-th rule08-th-center">1550nm</th>`;
    message += `<th class="rule08-th rule08-th-center">Statut</th>`;
    message += `</tr>`;
    message += `</thead>`;
    message += `<tbody>`;

    const analysis = this._analyzeEventsForTable(issues);

    // Lignes pour outliers
    analysis.outliers.forEach((outlier) => {
      const values1310 = outlier.values.find((v) => v.wavelength === "1310nm");
      const values1550 = outlier.values.find((v) => v.wavelength === "1550nm");
      const eventIndex = values1310?.eventIndex ?? values1550?.eventIndex ?? 0;

      message += `<tr class="rule08-outlier-row">`;
      message += `<td class="rule08-td"><strong>${outlier.fiberId}</strong></td>`;
      message += `<td class="rule08-td rule08-td-center">Event ${eventIndex + 1}</td>`;
      message += `<td class="rule08-td rule08-td-center">${values1310?.value.toFixed(1) || "-"}m</td>`;
      message += `<td class="rule08-td rule08-td-center">${values1550?.value.toFixed(1) || "-"}m</td>`;
      message += `<td class="rule08-td rule08-td-center rule08-error-status">❌ À vérifier (écart: ${outlier.maxDeviation.toFixed(1)}m)</td>`;
      message += `</tr>`;
    });

    // Ligne pour consensus
    if (analysis.consensus.count > 0) {
      const eventIndex = issues[0]?.eventIndex ?? 0;

      message += `<tr class="rule08-consensus-row">`;
      message += `<td class="rule08-td">${analysis.consensus.count} autres fibres</td>`;
      message += `<td class="rule08-td rule08-td-center">Event ${eventIndex + 1}</td>`;
      message += `<td class="rule08-td rule08-td-center">~${analysis.consensus.center1310.toFixed(1)}m</td>`;
      message += `<td class="rule08-td rule08-td-center">~${analysis.consensus.center1550.toFixed(1)}m</td>`;
      message += `<td class="rule08-td rule08-td-center rule08-success-status">✅ Consensus</td>`;
      message += `</tr>`;
    }

    message += `</tbody>`;
    message += `</table><br>`;

    message += `<strong>⚡ Action requise :</strong> Vérifier uniquement les fibres marquées ❌`;

    return message;
  }

  /**
   * Générer le message pour erreurs de variance de longueurs (FORMAT TABLEAU)
   */
  static _generateLengthVarianceMessage(issues, pboValue) {
    // Utilise la même logique que les positions mais avec titre adapté
    let message = this._generatePositionVarianceMessage(issues, pboValue);

    // Remplacer le titre pour les longueurs
    message = message.replace("Événements incohérents", "Longueurs d'événements incohérentes");

    return message;
  }

  /**
   * Ajouter une erreur au résultat d'une fibre
   */
  static _addError(fiber, rule, title, message) {
    if (!fiber.testResults) {
      fiber.testResults = {
        status: "success",
        hasError: false,
        messages: [],
      };
    }

    fiber.testResults.status = "error";
    fiber.testResults.hasError = true;
    fiber.testResults.messages.push({
      type: "error",
      rule,
      title,
      message,
    });
  }
}
